import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { login, getProfile, refreshToken, isLoggedIn } from './authService';
import { RootState } from '../../store';
import { clearAllSessionStorage } from '../../../services/sessionStorage';
import { USER_TYPES } from '../../../services/userRoles';

export interface Profile {
  id: any;
  address: any;
  country: any;
  result_filters: any[];
  feedback_filters: any[];
  organisation_filters: any[]
}
export interface UserProfile {
  id: number | undefined;
  first_name: string;
  last_name: string;
  email: string;
  organisation: number | undefined;
  organisation_name: string;
  is_superuser: boolean;
  is_staff: boolean;
  is_active: boolean;
  date_joined: string;
  last_login: string;
  user_type: string;
  profile: Profile;
}

export interface AuthState {
  access: string | null;
  isAuthenticated: boolean;
  user: UserProfile | null;
  loading: boolean;
  error: string | null;
  isSuperUser: boolean;
  isAppUser: boolean;
  isPortalUser: boolean;
  refreshing: boolean;
}

interface LoginPayload {
  username: string;
  password: string;
}

interface LoginResponse {
  access: string;
}

const initialState: AuthState = {
  access: null,
  isAuthenticated: false,
  user: null,
  loading: false,
  error: null,
  isSuperUser: false,
  isAppUser: false,
  isPortalUser: false,
  refreshing: false,
};

// Login User and save access and refresh tokens in auth state
export const loginUser = createAsyncThunk<LoginResponse, LoginPayload, { state: RootState }>(
  'auth/loginUser',
  async ({ username, password }, { dispatch, rejectWithValue }) => {
    try {
      const data = await login(username, password);
      const { access } = data;

      dispatch(setCredentials({ access }));
      await dispatch(fetchUserProfile()).unwrap();

      return { access };
    } catch (error: any) {
      // A token alone is not a complete portal login: a permitted profile must load.
      dispatch(logout());
      return rejectWithValue(error.response?.data || error?.message || 'Unknown error during login');
    }
  }
);

// Share a refresh across concurrent API requests, including server-rejected tokens.
let pendingRefresh: Promise<void> | null = null;
export const refreshAccessToken = createAsyncThunk<void, boolean | undefined, { state: RootState }>(
  'auth/refreshAccessToken',
  async (force, { getState, dispatch, rejectWithValue }) => {
    const access = getState().auth.access;
    if (!pendingRefresh && !force && isLoggedIn(access)) return;
    if (!pendingRefresh) {
      dispatch(refreshAccessTokenTrue());
      pendingRefresh = (async () => {
        try {
          const data = await refreshToken();
          // A refresh must not restore a session that was logged out or replaced.
          if (getState().auth.access !== access || !getState().auth.isAuthenticated) {
            throw Object.assign(new Error('Sign in again to continue.'), { response: { status: 401 } });
          }
          if (typeof data.refresh === 'string' && data.refresh) localStorage.setItem('refresh', data.refresh);
          dispatch(setCredentials({ access: data.access }));
        } catch (error: any) {
          if ((error.response?.status === 401 || error.response?.status === 403) && getState().auth.access === access) {
            dispatch(logout());
          }
          throw error;
        } finally {
          dispatch(refreshAccessTokenFalse());
          pendingRefresh = null;
        }
      })();
    }
    try {
      await pendingRefresh;
    } catch (error: any) {
      // Preserve useful error categories without storing request headers or tokens.
      return rejectWithValue({ message: error.message, code: error.code,
        response: error.response ? { status: error.response.status } : undefined });
    }
  }
);


// Handles adding profile information to auth state.  Happens during LoginUser above
export const fetchUserProfile = createAsyncThunk<UserProfile, void, { state: RootState }>(
  'auth/fetchUserProfile',
  async (_, { getState, rejectWithValue, dispatch }) => {
    const state = getState();
    const access = state.auth.access;

    if (access) {
      try {
        const userProfile = await getProfile(access);
        // A late profile response must not restore a logged-out or replaced session.
        if (!getState().auth.isAuthenticated || getState().auth.access !== access) {
          return rejectWithValue('Session changed while loading the profile');
        }
        return userProfile;
      } catch (error: any) {
        return rejectWithValue(error.response?.data || 'Unknown error fetching user profile');
      }
    } else {
      return rejectWithValue('Access token not found');
    }
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setCredentials: (state, action: PayloadAction<{ access: string }>) => {
      state.access = action.payload.access;
      state.isAuthenticated = true;
    },
    setIsAuthenticated: (state, action: PayloadAction<boolean>) => {
      state.isAuthenticated = action.payload;
    },
    logout: (state) => {
      console.log("logout: clearing local storage")
      localStorage.clear();
      clearAllSessionStorage();
      Object.assign(state, initialState);
    },
    refreshAccessTokenTrue: (state) => {
      state.refreshing = true;
    },
    refreshAccessTokenFalse: (state) => {
      state.refreshing = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loginUser.pending, (state) => {
        state.loading = true;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.error = action.payload as string | null;
        state.loading = false;
      })
      .addCase(fetchUserProfile.fulfilled, (state, action) => {
        state.user = action.payload;
        state.isAuthenticated = true;
        state.isSuperUser = action.payload.is_superuser;
        state.isPortalUser = action.payload.user_type === USER_TYPES.portalUser;
        state.isAppUser = action.payload.user_type === USER_TYPES.appUser;
      })
      .addCase(fetchUserProfile.rejected, (state, action) => {
        state.error = action.payload as string | null;
      })
      .addCase(refreshAccessToken.fulfilled, (state) => {
        state.error = null;
      })
      .addCase(refreshAccessToken.rejected, (state, action) => {
        state.error = (action.payload as { message?: string } | undefined)?.message || 'Unable to refresh your access. Please try again.';
      });

  },
});

export const { setCredentials, setIsAuthenticated, logout, refreshAccessTokenTrue, refreshAccessTokenFalse } = authSlice.actions;
export default authSlice.reducer;

// SNIPPETS TO USE

// FETCH AUTH DATA
// const dispatch = useDispatch<AppDispatch>();
// const authData = useSelector((state: RootState) => state.auth); // Accessing the auth state
// console.log(authData.access)
// console.log(authData.user.id)

// LOGOUT
// const dispatch = useDispatch<AppDispatch>();
//     const handleLogout = () => {
//     dispatch(logout());
// };

// LOGIN
// const dispatch = useDispatch<AppDispatch>();
// const resultAction = await dispatch(loginUser({ username, password }));
// unwrapResult(resultAction); // If the promise is rejected, this will throw and you wont proceed

// AUTHENTICATION CHECK - No longer Needed
// const dispatch = useDispatch<AppDispatch>();
// useEffect(() => {
//   if (!authData.isAuthenticated) {
//       navigate(loginpage);
//       return;
//   }
//   if (!isLoggedIn()) {
//       dispatch(refreshAccessToken());
//   }
// }, [dispatch, navigate, authData.isAuthenticated]);
